import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import hljs from 'highlight.js/lib/core';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import scss from 'highlight.js/lib/languages/scss';
import { DemoSources } from '../demos/demos.model';

// Регистрируем только нужные языки (core-сборка highlight.js — компактный бандл, без DI-провайдера).
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('scss', scss);

type Tab = 'ts' | 'html' | 'scss';

/** Всегда развёрнутый блок исходников примера: табы TS/HTML/SCSS, подсветка, копирование. */
@Component({
  selector: 'app-demo-code',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="demo-code">
      <div class="demo-code__bar">
        <button type="button"
                *ngFor="let t of tabs"
                class="demo-code__tab"
                [class.demo-code__tab--active]="t === active"
                (click)="select(t)">{{ t | uppercase }}</button>
        <span class="demo-code__spacer"></span>
        <button type="button" class="demo-code__copy" (click)="copy()">Copy</button>
      </div>

      <p *ngIf="loading" class="demo-code__msg">Loading…</p>
      <p *ngIf="error" class="demo-code__msg demo-code__msg--err">{{ error }}</p>
      <pre *ngIf="!loading && !error"><code class="hljs" [innerHTML]="highlighted"></code></pre>
    </div>
  `,
  styles: [`
    .demo-code { margin-top: 16px; }
    .demo-code__bar { display: flex; align-items: center; gap: 4px; margin: 8px 0 0; }
    .demo-code__tab { cursor: pointer; background: #f3f3f3; border: 1px solid #ccc; border-bottom: none; padding: 4px 12px; }
    .demo-code__tab--active { background: #fff; font-weight: 600; }
    .demo-code__spacer { flex: 1; }
    .demo-code__copy { cursor: pointer; background: none; border: 1px solid #ccc; border-radius: 4px; padding: 4px 10px; }
    .demo-code pre { margin: 0; max-height: 90vh; overflow: auto; border: 1px solid #ccc; }
    .demo-code__msg { padding: 8px; }
    .demo-code__msg--err { color: #b00; }
  `],
})
export class DemoCodeViewerComponent {
  private _sources: DemoSources | undefined;

  /**
   * Пути к исходникам примера. Сеттер (а не ngOnChanges) — чтобы табы/активная вкладка
   * пересчитывались и загрузка стартовала при обычном присваивании поля (в т.ч. в тестах
   * без host-обёртки), не только при биндинге через шаблон.
   */
  @Input()
  set sources(value: DemoSources) {
    this._sources = value;
    this.tabs = (['ts', 'html', 'scss'] as Tab[]).filter(t => !!value?.[t]);
    this.active = this.tabs[0] ?? 'ts';
    this.cache.clear();
    this.code = '';
    this.load();
  }
  get sources(): DemoSources {
    return this._sources as DemoSources;
  }

  active: Tab = 'ts';
  tabs: Tab[] = [];
  code = '';
  loading = false;
  error = '';

  private cache = new Map<string, string>();
  private readonly langMap: Record<Tab, string> = { ts: 'typescript', html: 'xml', scss: 'scss' };

  constructor(private readonly http: HttpClient) {}

  get lang(): string { return this.langMap[this.active]; }

  /** HTML с подсветкой highlight.js. Безопасен для [innerHTML]: hljs экранирует ввод, Angular-санитайзер сохраняет span.class токенов. */
  get highlighted(): string {
    return this.code ? hljs.highlight(this.code, { language: this.lang }).value : '';
  }

  select(tab: Tab): void {
    this.active = tab;
    this.load();
  }

  copy(): void {
    navigator.clipboard?.writeText(this.code);
  }

  private load(): void {
    const path = this.sources?.[this.active];
    if (!path) { this.code = ''; return; }
    const cached = this.cache.get(path);
    if (cached !== undefined) { this.code = cached; return; }

    this.loading = true;
    this.error = '';
    this.http.get('demo-src/' + path, { responseType: 'text' }).subscribe({
      next: txt => { this.cache.set(path, txt); this.code = txt; this.loading = false; },
      error: () => { this.error = 'Failed to load source'; this.loading = false; },
    });
  }
}
